import { Injectable } from "@angular/core";
import { DefaultTranspiler, TranslocoTranspiler, TranspileParams } from "@jsverse/transloco";
import { IntlMessageFormat } from "intl-messageformat";
import { PSEUDO_LANG } from "./transloco-pseudo.interceptor";

// Why this exists instead of `@jsverse/transloco-messageformat`:
//
// The official messageformat plugin is built on the `messageformat` library, which compiles ICU
// strings to JavaScript at runtime via `new Function()`. Our deployed CSP is `script-src 'self'`
// with NO `unsafe-eval` (see Cerrix.App/Extensions/SecurityExtensions.cs), so that plugin throws
// `EvalError: call to Function() blocked by CSP` on dev.cerrix.com. Relaxing the CSP to allow
// `unsafe-eval` would weaken XSS protection across the whole origin.
//
// `intl-messageformat` (FormatJS) parses ICU into an AST and evaluates it directly, no `eval`, no
// `Function()`, so it runs under the strict CSP unchanged. This transpiler is the do-it-yourself
// equivalent of the (still-unmerged) transloco PR #649. It keeps full ICU support (plural/select/
// number/date) using the exact same single-brace syntax the docs assume.
// Refs: jsverse/transloco#796, jsverse/transloco#649, messageformat/messageformat#180.
@Injectable({ providedIn: "root" })
export class IntlMessageFormatTranspiler extends DefaultTranspiler implements TranslocoTranspiler {
    // IntlMessageFormat needs a real BCP-47 locale for correct plural/select rules. The active lang
    // is tracked via onLangChanged (fired by TranslocoService on every setActiveLang, including init);
    // we seed it from config so the very first transpile before any switch is still correct.
    private locale = this.toIntlLocale(this.config.defaultLang);

    // Parsing ICU is not free; cache the compiled formatter per (locale, message). Keyed with a NUL
    // separator since neither a locale nor a message can contain the NUL character.
    private readonly cache = new Map<string, IntlMessageFormat>();

    // Declared on the TranslocoTranspiler interface (not on DefaultTranspiler), so no `override`.
    onLangChanged(lang: string): void {
        this.locale = this.toIntlLocale(lang);
    }

    override transpile(params: TranspileParams): unknown {
        const { value } = params;
        // Only ICU-bearing strings need FormatJS; a `{` is the cheap, reliable signal. Plain strings,
        // objects and arrays fall through to DefaultTranspiler, whose object/array handling recurses
        // back into this.transpile — so nested ICU strings are still routed here.
        if (typeof value === "string" && value.includes("{")) {
            return this.format(value, params.params ?? {});
        }
        return super.transpile(params);
    }

    private format(message: string, params: Record<string, unknown>): string {
        const cacheKey = `${this.locale}\u0000${message}`;
        try {
            let formatter = this.cache.get(cacheKey);
            if (!formatter) {
                formatter = new IntlMessageFormat(message, this.locale);
                this.cache.set(cacheKey, formatter);
            }
            return formatter.format(params) as string;
        } catch {
            // Malformed ICU shouldn't take down the whole render. Return the raw string so the key is
            // still visible (and the mistake obvious) rather than throwing mid-template.
            return message;
        }
    }

    // The dev-only `pseudo` locale carries English content (visually mangled by PseudoInterceptor); it
    // is not a valid BCP-47 tag, so map it to `en` for plural/select rule resolution.
    private toIntlLocale(lang: string): string {
        return lang === PSEUDO_LANG ? "en" : lang;
    }
}
